import { useEffect, useRef, useState } from 'react';
import { SplatInfo, TryAgainMotivation } from '../Storage/TextBase';
import gsap from 'gsap';

const Splat = () => {

  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameState, setGameState] = useState('menu'); // menu, running, paused, gameover
  const [inti, setInti] = useState(1100);

  const playerRef = useRef(null);
  const bulletsRef = useRef([]);
  const enemiesRef = useRef([]);
  const particlesRef = useRef([]);
  const animationIdRef = useRef(null);

  const friction = 0.99;
  let startY = 0;

  useEffect(() => {
    setInti(/Mobi|Android/i.test(navigator.userAgent) ? 1200 : 1100);
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const c = canvas.getContext('2d');
    init(c);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      reinit(canvas);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Classes
  class Player {
    constructor(x, y, radius, color) {
      this.x = x;
      this.y = y;
      this.radius = radius;
      this.color = color;
    }

    pencil(c) {
      c.beginPath();
      c.arc(this.x, this.y, this.radius, 0, Math.PI * 2, false);
      c.fillStyle = this.color;
      c.fill();
    }
  }

  class Bullet {
    constructor(x, y, radius, color, velocity) {
      this.x = x;
      this.y = y;
      this.radius = radius;
      this.color = color;
      this.velocity = velocity;
    }

    pencil(c) {
      c.beginPath();
      c.arc(this.x, this.y, this.radius, 0, Math.PI * 2, false);
      c.fillStyle = this.color;
      c.fill();
    }

    update(c) {
      this.pencil(c);
      this.x += this.velocity.x;
      this.y += this.velocity.y;
    }
  }

  class Enemy {
    constructor(x, y, radius, color, velocity) {
      this.x = x;
      this.y = y;
      this.radius = radius;
      this.color = color;
      this.velocity = velocity;
    }

    pencil(c) {
      c.beginPath();
      c.arc(this.x, this.y, this.radius, 0, Math.PI * 2, false);
      c.fillStyle = this.color;
      c.fill();
    }

    update(c) {
      this.pencil(c);
      this.x += this.velocity.x;
      this.y += this.velocity.y;
    }
  }

  class Particle {
    constructor(x, y, radius, color, velocity) {
      this.x = x;
      this.y = y;
      this.radius = radius;
      this.color = color;
      this.velocity = velocity;
      this.alpha = 1;
    }

    pencil(c) {
      c.save();
      c.globalAlpha = this.alpha;
      c.beginPath();
      c.arc(this.x, this.y, this.radius, 0, Math.PI * 2, false);
      c.fillStyle = this.color;
      c.fill();
      c.restore();
    }

    update(c) {
      this.pencil(c);
      this.velocity.x *= friction;
      this.velocity.y *= friction;
      this.x += this.velocity.x;
      this.y += this.velocity.y;
      this.alpha -= 0.01;
    }
  }

  function init(c) {
    const canvas = canvasRef.current;
    const x = canvas.width / 2;
    const y = canvas.height / 2;
    playerRef.current = new Player(x, y, 15, 'white');
    bulletsRef.current = [];
    enemiesRef.current = [];
    particlesRef.current = [];
    setScore(0);
  };

  function reinit() {
    const x = canvasRef.current.width / 2;
    const y = canvasRef.current.height / 2;
    playerRef.current = new Player(x, y, 15, 'white');
    bulletsRef.current = [];
    enemiesRef.current = [];
    particlesRef.current = [];
  };

  function spawnEnemies() {
    const intervalId = setInterval(() => {
      const canvas = canvasRef.current;
      const radius = Math.random() * (30 - 6) + 6;

      let x, y;
      if (Math.random() < 0.5) {
        x = Math.random() < 0.5 ? 0 - radius : canvas.width + radius;
        y = Math.random() * canvas.height;
      } else {
        x = Math.random() * canvas.width;
        y = Math.random() < 0.5 ? 0 - radius : canvas.height + radius;
      }

      const color = `hsl(${Math.random() * 360}, 50%, 50%)`;

      const angle = Math.atan2(
        canvas.height / 2 - y,
        canvas.width / 2 - x
      );

      const velocity = {
        x: Math.cos(angle),
        y: Math.sin(angle),
      };

      enemiesRef.current.push(new Enemy(x, y, radius, color, velocity));
    }, inti);

    return intervalId;
  };

  function animate() {
    const canvas = canvasRef.current;
    const c = canvas.getContext('2d');

    animationIdRef.current = requestAnimationFrame(animate);
    c.clearRect(0, 0, canvas.width, canvas.height);
    c.fillStyle = '#0000001a';
    c.fillRect(0, 0, canvas.width, canvas.height);

    playerRef.current?.pencil(c);

    particlesRef.current.forEach((particle, index) => {
      if (particle.alpha <= 0) {
        particlesRef.current.splice(index, 1);
      } else {
        particle.update(c);
      }
    });

    bulletsRef.current.forEach((bullet, index) => {
      bullet.update(c);
      if (
        bullet.x + bullet.radius < 0 ||
        bullet.x - bullet.radius > canvas.width ||
        bullet.y + bullet.radius < 0 ||
        bullet.y - bullet.radius > canvas.height
      ) {
        setTimeout(() => {
          bulletsRef.current.splice(index, 1);
        }, 0);
      }
    });

    enemiesRef.current.forEach((enemy, index) => {
      enemy.update(c);

      const dist = Math.hypot(
        playerRef.current.x - enemy.x,
        playerRef.current.y - enemy.y
      );

      // Game Over
      if (dist - enemy.radius - playerRef.current.radius < 1) {
        cancelAnimationFrame(animationIdRef.current);
        setGameState('gameover');
      }

      bulletsRef.current.forEach((bullet, bulletIndex) => {
        const dist = Math.hypot(bullet.x - enemy.x, bullet.y - enemy.y);

        // Bullet hits Enemy
        if (dist - enemy.radius - bullet.radius < 1) {
          for (let i = 0; i < enemy.radius * 2; i++) {
            particlesRef.current.push(
              new Particle(
                bullet.x,
                bullet.y,
                Math.random() * 2,
                enemy.color,
                {
                  x: (Math.random() - 0.5) * (Math.random() * 6),
                  y: (Math.random() - 0.5) * (Math.random() * 6),
                }
              )
            );
          }

          if (enemy.radius - 10 > 8) {
            setScore((prev) => prev + 10);
            gsap.to(enemy, {
              radius: enemy.radius - 10,
            });
            setTimeout(() => {
              bulletsRef.current.splice(bulletIndex, 1);
            }, 0);
          } else {
            setScore((prev) => prev + 25);
            setTimeout(() => {
              enemiesRef.current.splice(index, 1);
              bulletsRef.current.splice(bulletIndex, 1);
            }, 0);
          }
        }
      });
    });
  };

  // Game Control
  function handleStart() {
    const c = canvasRef.current.getContext('2d');
    setGameState('running');
    init(c);
    animate();
    spawnEnemies();
  };

  function handleStop() {
    cancelAnimationFrame(animationIdRef.current);
    window.location.reload();
  }

  // Input Events
  useEffect(() => {

    const handleClick = (event) => {
      const canvas = canvasRef.current;
      const angle = Math.atan2(
        event.clientY - canvas.height / 2,
        event.clientX - canvas.width / 2
      );
      const velocity = {
        x: Math.cos(angle) * 4,
        y: Math.sin(angle) * 4,
      };
      bulletsRef.current.push(
        new Bullet(canvas.width / 2, canvas.height / 2, 5, 'white', velocity)
      );
    };

    const handleTouch = (event) => {
      const touch = event.touches[0];
      const canvas = canvasRef.current;
      const angle = Math.atan2(
        touch.clientY - canvas.height / 2,
        touch.clientX - canvas.width / 2
      );
      const velocity = {
        x: Math.cos(angle) * 4,
        y: Math.sin(angle) * 4,
      };
      bulletsRef.current.push(
        new Bullet(canvas.width / 2, canvas.height / 2, 5, 'white', velocity)
      );
    };

    window.addEventListener('click', handleClick);
    window.addEventListener('touchmove', handleTouch);
    window.addEventListener('contextmenu', (e) => e.preventDefault());

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('touchmove', handleTouch);
      window.removeEventListener('contextmenu', (e) => e.preventDefault());
      window.removeEventListener('touchstart', function (e) {
        startY = e.touches[0].clientY;
      }, { passive: false });
      window.removeEventListener('touchmove', function (e) {
        const currentY = e.touches[0].clientY;
        const scrollingElement = document.scrollingElement || document.body;
        if (scrollingElement.scrollTop === 0 && currentY > startY) {
          e.preventDefault();
        }
      }, { passive: false });

    };

  }, []);

  return (
    <>
        {
            gameState === 'menu' ? 
            (
                <>
                    <div style={{position: 'absolute', zIndex: 10, display: 'grid', width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)'}}>
                        <div className='splat-menu'>
                            <h1 className='splat-title'>Welcome to Splat!</h1>
                            <p >Use your mouse or touch to shoot splats!</p>
                            <p>{SplatInfo()}</p>
                            <button onClick={handleStart} className='splat-button'>Start Game</button>
                            <button onClick={handleStop} className='splat-button'>Exit Game</button>
                        </div>
                    </div>
                </>
            ) : gameState === 'gameover' ? 
            (
                <>
                    <div style={{position: 'absolute', zIndex: 10, display: 'grid', width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)'}}>
                        <div className='splat-menu'>
                            <h1 className='splat-title'>Welcome to Splat!</h1>
                            <p >{TryAgainMotivation()}</p>
                            <br></br>
                            <h3 style={{color: 'white'}}>Score: {score}</h3>
                            <button onClick={handleStart} className='splat-button'>Start Game</button>
                            <button onClick={handleStop} className='splat-button'>Exit Game</button>
                        </div>
                    </div>
                </>
            ) : null
        }
        <div className='splat-container no-select'>
            <canvas ref={canvasRef} className='splat-canvas' />
        </div>
    </>
  );
    
};

export { Splat };
